import type {
  Commonv1Value,
  TypeDate,
  TypeDateTime,
  TypeTimeOfDay,
  V1ValueType,
} from '@/generated/hooks/grimoire/models';
import { encodeNumber } from './numberConversion';
import { isFileListType, unwrapType } from './typeDetection';
import { formatNumber } from '@/modules/outputs/utils';
import {
  formatDateISO,
  formatDateTimeISO,
  formatTimeISO,
  parseDate,
  parseDateTime,
  parseTime,
} from '@/shared/utils/datetime-utils';

/**
 * Converts a Commonv1Value to a display value (string or boolean)
 */
export function toDisplayValue(value?: Commonv1Value): string | boolean {
  if (!value) return '';

  // Text
  if (value.text !== undefined) {
    return value.text;
  }

  // Boolean
  if (value.bool_value !== undefined) {
    return value.bool_value;
  }

  // Number - use proper decimal decoding
  if (value.number) {
    return formatNumber(value.number);
  }

  // Date
  if (value.date) {
    return formatDate(value.date);
  }

  // DateTime
  if (value.datetime) {
    return formatDateTime(value.datetime);
  }

  // Time of day
  if (value.timeofday) {
    return formatTime(value.timeofday);
  }

  // Duration
  if (value.duration) {
    return value.duration;
  }

  // File
  if (value.file) {
    return value.file.inline?.name || value.file.remote || '';
  }

  // Complex types - JSON stringify
  if (value.list) {
    // Check if it's a list of files
    const allFiles = value.list.items?.every(item => item.file);
    if (allFiles && value.list.items && value.list.items.length > 0) {
      // Return JSON array of file resource names for file lists
      const fileNames = value.list.items.map(
        item => item.file?.remote || item.file?.inline?.name || '',
      );
      return JSON.stringify(fileNames);
    }
    return JSON.stringify(value.list, null, 2);
  }

  if (value.dictionary) {
    return JSON.stringify(value.dictionary, null, 2);
  }

  if (value.table) {
    return JSON.stringify(value.table, null, 2);
  }

  if (value.set) {
    return JSON.stringify(value.set, null, 2);
  }

  return '';
}

/**
 * Formats a date to YYYY-MM-DD
 */
export function formatDate(date: TypeDate): string {
  return formatDateISO(date);
}

/**
 * Formats a datetime to YYYY-MM-DDTHH:mm
 */
export function formatDateTime(datetime: TypeDateTime): string {
  return formatDateTimeISO(datetime);
}

/**
 * Formats a time to HH:mm
 */
export function formatTime(time: TypeTimeOfDay): string {
  return formatTimeISO(time);
}

/**
 * Parses a date string (YYYY-MM-DD) to TypeDate
 * Re-exported from @/shared/utils/datetime-utils for backward compatibility
 */
export { parseDate } from '@/shared/utils/datetime-utils';

/**
 * Parses a datetime string (YYYY-MM-DDTHH:mm) to TypeDateTime
 * Re-exported from @/shared/utils/datetime-utils for backward compatibility
 */
export { parseDateTime } from '@/shared/utils/datetime-utils';

/**
 * Parses a time string (HH:mm) to TypeTimeOfDay
 * Re-exported from @/shared/utils/datetime-utils for backward compatibility
 */
export { parseTime } from '@/shared/utils/datetime-utils';

/**
 * Extracts the resource name from a file value
 *
 * Handles both new JSON format {name, resourceName} and old string format
 * for backward compatibility. Tries to parse as JSON first, falling back
 * to treating the value as a plain string.
 *
 * @param value - The file value to extract from
 * @returns The resource name string
 */
function extractFileResourceName(value: string): string {
  try {
    const parsed = JSON.parse(value);
    // New format: {name: "file.pdf", resourceName: "organizations/.../files/123"}
    if (parsed && typeof parsed === 'object' && parsed.resourceName) {
      return parsed.resourceName;
    }
  } catch {
    // Not JSON, return as-is (backwards compatibility)
  }
  return value;
}

/**
 * Converts a display value to Commonv1Value based on type
 *
 * Transforms user-entered values (strings or booleans) into the appropriate
 * Commonv1Value format based on the input type. Handles all supported types
 * including text, numbers, dates, files, enums, and complex JSON structures.
 *
 * @param value - The display value to convert (string or boolean)
 * @param valueType - The V1ValueType defining the type structure
 * @returns Commonv1Value object with the appropriate field set
 */
export function toCommonValue(
  value: string | boolean,
  valueType: V1ValueType | string,
): Commonv1Value {
  // Handle legacy string-based type names
  if (typeof valueType === 'string') {
    switch (valueType) {
      case 'text':
        return { text: value as string };
      case 'number':
        return { number: encodeNumber(value as string) };
      case 'boolean':
        return { bool_value: value as boolean };
      case 'date':
        return value ? { date: parseDate(value as string) } : {};
      case 'datetime':
        return value ? { datetime: parseDateTime(value as string) } : {};
      case 'time':
        return value ? { timeofday: parseTime(value as string) } : {};
      case 'none':
        return { null_value: 'NULL_VALUE' };
      case 'file':
        return { file: { remote: extractFileResourceName(value as string) } };
      case 'fileList':
        return parseFileList(value as string);
      case 'enum':
        return { text: value as string };
      case 'complex':
        // TODO: dictionary, table, union, opaque and generic_dictionary inputs
        // are still submitted as a JSON string rather than their real wire
        // shapes (Dictionary = {entries:[{key,value}]}, TableValue = feather-v2
        // inline/remote). Needs structured editors per type — see
        // `resolveComponentType` in inputProcessing.ts.
        return { text: value as string };
      default:
        return { text: value as string };
    }
  }

  // Handle V1ValueType objects
  if (valueType.scalar_type) {
    switch (valueType.scalar_type) {
      case 'VALUE_SCALAR_TYPE_TEXT':
        return { text: value as string };
      case 'VALUE_SCALAR_TYPE_NUMBER':
        return { number: encodeNumber(value as string) };
      case 'VALUE_SCALAR_TYPE_BOOLEAN':
        // List elements round-trip through display strings, so accept both.
        return {
          bool_value: typeof value === 'boolean' ? value : value === 'true',
        };
      case 'VALUE_SCALAR_TYPE_DATE':
        return value ? { date: parseDate(value as string) } : {};
      case 'VALUE_SCALAR_TYPE_DATETIME':
        return value ? { datetime: parseDateTime(value as string) } : {};
      case 'VALUE_SCALAR_TYPE_TIME':
        return value ? { timeofday: parseTime(value as string) } : {};
      case 'VALUE_SCALAR_TYPE_FILE':
        return { file: { remote: extractFileResourceName(value as string) } };
      default:
        return { text: value as string };
    }
  }

  if (valueType.none_type) {
    return { null_value: 'NULL_VALUE' };
  }

  // Handle list types
  if (valueType.list_type) {
    // Check if it's a list of files
    if (isFileListType(valueType)) {
      return parseFileList(value as string);
    }
    // Generic list - parse JSON.
    // NOTE: currently unreachable. The JSON textarea converts via the legacy
    // `'complex'` kind, and `listToCommonValue` only ever passes a leaf element
    // type, so nothing calls this with a `list_type`. Kept correct for when
    // ENG-4913 wires the textarea through the typed branch: `Value.list` is a
    // `List` message (`{items: [Value]}`), and assigning a bare array to `list`
    // leaves `items` undefined, which `commonValueToRainTaggedValue` reads as an
    // empty list.
    try {
      const parsed = JSON.parse(value as string);
      if (Array.isArray(parsed)) {
        return listToCommonValue(
          parsed.map(item => (typeof item === 'string' ? item : String(item))),
          unwrapType(valueType.list_type.inner).baseType,
        );
      }
      // `toDisplayValue` renders a list as its wire shape (`{"items":[…]}`), so
      // a value round-tripped through the JSON textarea arrives already
      // element-wrapped and can be passed straight back through.
      if (parsed && Array.isArray(parsed.items)) {
        return { list: parsed };
      }
      return { text: value as string };
    } catch {
      return { text: value as string };
    }
  }

  // Handle dictionary types
  if (valueType.dictionary_type) {
    try {
      const parsed = JSON.parse(value as string);
      return { dictionary: parsed };
    } catch {
      return { text: value as string };
    }
  }

  // Handle table types
  if (valueType.table_type) {
    try {
      const parsed = JSON.parse(value as string);
      return { table: parsed };
    } catch {
      return { text: value as string };
    }
  }

  // Default to text
  return { text: value as string };
}

/**
 * Builds a `List` value from element display values.
 *
 * Each element is converted with the list's element type, so `List[number]`
 * yields `{list:{items:[{number:…}]}}` rather than a list of text. The `items`
 * wrapper matters: `Value.list` is a `List` message (`{items: [Value]}`), not a
 * bare array.
 *
 * @param items - Element display values, in order
 * @param elementType - The list's unwrapped element type
 * @returns A Commonv1Value wrapping the converted elements
 */
export function listToCommonValue(
  items: string[],
  elementType: V1ValueType,
): Commonv1Value {
  return {
    list: {
      items: items.map(item => toCommonValue(item, elementType)),
    },
  };
}

/**
 * Parses a JSON file list string into a Commonv1Value list of files
 */
function parseFileList(value: string): Commonv1Value {
  try {
    const fileNames = JSON.parse(value);
    if (Array.isArray(fileNames)) {
      return {
        list: {
          items: fileNames.map(item => {
            // Handle both {name, resourceName} objects and plain strings
            let resourceName: string;
            if (typeof item === 'object' && item.resourceName) {
              resourceName = item.resourceName;
            } else if (typeof item === 'string') {
              resourceName = item;
            } else {
              resourceName = '';
            }
            return { file: { remote: resourceName } };
          }),
        },
      };
    }
  } catch {
    // If parsing fails, return empty list
    return { list: { items: [] } };
  }
  return { list: { items: [] } };
}
